if (!customElements.get("grouped-variant-picker")) {
  class GroupedVariantPicker extends HTMLElement {
    constructor() {
      super();

      /* ── Parse the sibling map embedded by Liquid ── */
      const mapScript = this.querySelector("script[data-grouped-variants-map]");
      if (!mapScript) {
        console.warn("GroupedVariantPicker: no sibling-map script found");
        this.siblingMap = { current: {}, siblings: {} };
      } else {
        try {
          this.siblingMap = JSON.parse(mapScript.textContent);
        } catch (e) {
          console.warn("GroupedVariantPicker: failed to parse sibling map", e);
          this.siblingMap = { current: {}, siblings: {} };
        }
      }

      /* ── Read attributes set by Liquid on the custom element ── */
      this.sectionId = this.dataset.section;
      this.currentProductId = this.dataset.productId;
      this.currentProductUrl = this.dataset.productUrl;

      /* ── Order the values inside each selector ── */
      this.sortOptionValues();

      /* ── Bind change / click listeners ── */
      this.addEventListener("change", this.onOptionChange.bind(this));
    }

    /* ────────────────────────────────────────────
       Compare two option values by the numbers they
       contain, in order.

         '5"'         → [5]
         '5.5"'       → [5.5]
         '3.5"x5.5"'  → [3.5, 5.5]

       Intl.Collator's `numeric` option can't do this
       on its own: for '5"' vs '5.5"' it matches the
       leading 5, then falls through to comparing '"'
       against '.', and ICU sorts '.' first — which
       is what put every .5 ahead of its .0.

       Values with no digits (Profile, Shape) yield
       empty keys and fall through to alphabetical.
    ──────────────────────────────────────────── */
    compareOptionValues(a, b) {
      const numbersIn = (value) =>
        (String(value).match(/\d+(?:\.\d+)?/g) || []).map(Number);

      const left = numbersIn(a);
      const right = numbersIn(b);

      for (let i = 0; i < Math.min(left.length, right.length); i++) {
        if (left[i] !== right[i]) return left[i] - right[i];
      }

      /* Shared prefix — the shorter one first, so 3.5" precedes 3.5"x5.5" */
      if (left.length !== right.length) return left.length - right.length;

      return this.collator.compare(a, b);
    }

    /* ────────────────────────────────────────────
       Liquid emits option values in whatever order
       the sibling collection happens to be sorted
       in, so sizes come out as "10, 7, 7.5, 8".
       Reorder each selector in place.
    ──────────────────────────────────────────── */
    sortOptionValues() {
      this.collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: "base",
      });

      this.querySelectorAll("fieldset.grouped-variant-fieldset").forEach(
        (fieldset) => {
          /* Colour arrives already ordered by catalog, not alphabetically —
             these values hold no digits, so sorting them here would split
             "Light Gray" from "Dark Gray" and push White to the end. */
          if (fieldset.hasAttribute("data-preserve-order")) return;

          /* Dropdown mode — reappending an <option> keeps the selection */
          const select = fieldset.querySelector("select");
          if (select) {
            Array.from(select.options)
              .sort((a, b) => this.compareOptionValues(a.value, b.value))
              .forEach((option) => select.appendChild(option));
            return;
          }

          /* Button mode — each value is an <input> + its <label> sibling,
             so the pair has to move together. Reappending a radio keeps
             its checked state. */
          const wrapper = fieldset.querySelector(".grouped-variant-buttons");
          if (!wrapper) return;

          Array.from(wrapper.querySelectorAll('input[type="radio"]'))
            .map((input) => ({
              input,
              label: wrapper.querySelector(
                `label[for="${CSS.escape(input.id)}"]`,
              ),
            }))
            .sort((a, b) =>
              this.compareOptionValues(a.input.value, b.input.value),
            )
            .forEach(({ input, label }) => {
              wrapper.appendChild(input);
              if (label) wrapper.appendChild(label);
            });
        },
      );
    }

    /* ────────────────────────────────────────────
       Gather currently-selected option values
    ──────────────────────────────────────────── */
    getSelectedOptions() {
      const selected = {};
      this.querySelectorAll("fieldset.grouped-variant-fieldset").forEach(
        (fs) => {
          const name = fs.dataset.optionName;
          const checked = fs.querySelector("input:checked");
          if (checked) {
            selected[name] = checked.value;
          } else {
            /* dropdown fallback */
            const sel = fs.querySelector("select");
            if (sel) selected[name] = sel.value;
          }
        },
      );
      return selected;
    }

    /* ────────────────────────────────────────────
       Build a lookup key from options (sorted by
       option-name so order doesn't matter)
    ──────────────────────────────────────────── */
    buildKey(opts) {
      /* Keys must match the Liquid-generated format: "Value1 / Value2 / ..." */
      return Object.values(opts).join(" / ");
    }

    /* ────────────────────────────────────────────
       When any option changes, find the matching
       sibling and swap the page content via AJAX.
       If the exact combo doesn't exist, pick the
       closest sibling that honours the user's
       changed option.
    ──────────────────────────────────────────── */
    onOptionChange(e) {
      const selected = this.getSelectedOptions();
      const key = this.buildKey(selected);
      let sibling = this.siblingMap.siblings[key];

      /* Exact match not found — find the closest sibling that
         matches the option the user just changed */
      if (!sibling) {
        const changedName = e?.target?.closest("fieldset")?.dataset?.optionName;
        const changedValue = e?.target?.value;
        sibling = this.findClosestSibling(selected, changedName, changedValue);
      }

      if (!sibling) return;

      /* If the matched sibling is the product already on the page, skip */
      if (String(sibling.productId) === String(this.currentProductId)) return;

      this.loadSibling(sibling);
    }

    /* ────────────────────────────────────────────
       Fetch the sibling product's section HTML
       and swap relevant DOM regions
    ──────────────────────────────────────────── */
    async loadSibling(sibling) {
      this.classList.add("loading");

      try {
        const url = `${sibling.url}?section_id=${this.sectionId}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();

        const doc = new DOMParser().parseFromString(html, "text/html");

        /* ── 1. Swap the grouped-variant-picker itself ── */
        const newPicker = doc.querySelector("grouped-variant-picker");
        if (newPicker) {
          /* Replace the entire custom element so its constructor re-runs
             and re-parses the new sibling map */
          this.replaceWith(newPicker);
        }

        /* ── 2. Sync the shipping-quote modal FIRST so the <modal-opener>
               we insert next can resolve its aria-controls target in its
               constructor (ModalOpener stores a reference to the modal at
               upgrade time and bails out if it's missing). ── */
        this.syncElement(doc, `#Shipping-Quote-Modal-${this.sectionId}`);

        /* ── 3. Swap buy buttons wrapper — covers both <product-form> and
               the Request-Shipping-Quote <modal-opener> variants ── */
        this.replaceElement(doc, `#BuyButtons-${this.sectionId}`);

        /* ── 4. Swap simple content areas via innerHTML ── */
        this.swapInner(doc, `#Price-${this.sectionId}`);
        this.swapInner(doc, `#Sku-${this.sectionId}`);
        this.swapInner(doc, ".product-title");
        this.swapInner(doc, ".product-info-description");
        this.swapInner(doc, `#Description-${this.sectionId}`);

        /* ── 4a. Replace the entire media-gallery element so its constructor
               re-runs and re-binds thumbnail click handlers to the new DOM ── */
        this.replaceElement(doc, `#Media-Gallery-${this.sectionId}`);

        /* ── 4b. Replace wrappers whose class toggles on metafield presence ── */
        this.replaceElement(doc, `#ShortDescription-${this.sectionId}`);
        this.replaceElement(doc, `#AdditionalInfoBlock-${this.sectionId}`);
        this.replaceElement(doc, `#PdfGuidesBlock-${this.sectionId}`);
        this.replaceElement(doc, `#Inventory-${this.sectionId}`);

        /* ── 4. Fallback: directly update the hidden variant id input ── */
        if (sibling.variantId) {
          document
            .querySelectorAll(
              `product-form[data-section="${this.sectionId}"] input[name="id"],
               #product-form-${this.sectionId} input[name="id"]`,
            )
            .forEach((input) => {
              input.value = sibling.variantId;
            });
        }

        /* ── 5. Update the browser URL without reload ── */
        if (sibling.url) {
          window.history.replaceState({}, "", sibling.url);
        }

        /* ── 6. Force-load lazy images in the swapped content. The theme's
               IntersectionObserver is initialized once on first load and
               doesn't observe nodes inserted later, so we promote
               data-srcset → srcset and mark them loaded directly. ── */
        document
          .querySelectorAll(
            "img.image-lazy-loading:not(.image-lazy-loaded)",
          )
          .forEach((img) => {
            if (img.hasAttribute("data-srcset")) {
              img.setAttribute("srcset", img.dataset.srcset);
              img.removeAttribute("data-srcset");
              if (img.complete && img.naturalWidth > 0) {
                img.classList.add("image-lazy-loaded");
              } else {
                img.addEventListener(
                  "load",
                  () => img.classList.add("image-lazy-loaded"),
                  { once: true },
                );
              }
            } else {
              img.classList.add("image-lazy-loaded");
            }
          });
      } catch (err) {
        console.error("GroupedVariantPicker: failed to load sibling", err);
      } finally {
        this.classList.remove("loading");
      }
    }

    /* ────────────────────────────────────────────
       Find the sibling that best matches the
       current selection. Prioritises keeping the
       option the user just changed, then picks
       the sibling sharing the most other values.
    ──────────────────────────────────────────── */
    findClosestSibling(selected, changedName, changedValue) {
      let best = null;
      let bestScore = -1;

      /* Weight each option by its position, so matching an earlier one outranks
         matching every later one put together. The score used to be a flat
         count of matches, which left ties to whichever sibling happened to come
         first in the map — arbitrary, and it moved when collection order did.
         The picker renders broadest-first (Profile, Shape, Size, Color), so
         position doubles as priority. */
      const names = this.siblingMap.optionNames || Object.keys(selected);
      const weightOf = (name) => {
        const index = names.indexOf(name);
        return index === -1 ? 0 : 1 << (names.length - 1 - index);
      };

      for (const sibling of Object.values(this.siblingMap.siblings)) {
        if (!sibling.options) continue;

        /* Must match the option the user explicitly changed */
        if (changedName && sibling.options[changedName] !== changedValue) continue;

        /* Score = the weight of the OTHER options that also match */
        let score = 0;
        for (const [name, value] of Object.entries(selected)) {
          if (name === changedName) continue;
          if (sibling.options[name] === value) score += weightOf(name);
        }

        if (score > bestScore) {
          bestScore = score;
          best = sibling;
        }
      }
      return best;
    }

    /* ── Helper: replace an element entirely (re-runs constructor) ── */
    replaceElement(doc, selector) {
      const oldEl = document.querySelector(selector);
      const newEl = doc.querySelector(selector);
      if (oldEl && newEl) {
        oldEl.replaceWith(newEl);
      }
    }

    /* ── Helper: sync an element that may appear or disappear between
       siblings (e.g. the shipping-quote modal). Replaces if both exist,
       inserts into the matching parent (preserving ancestor styles like
       color scheme) if only the new one exists, removes if only the old
       one exists. ── */
    syncElement(doc, selector) {
      const oldEl = document.querySelector(selector);
      const newEl = doc.querySelector(selector);
      if (oldEl && newEl) {
        oldEl.replaceWith(newEl);
      } else if (newEl) {
        // Walk up from newEl in the parsed doc to find an ancestor with an
        // id, then attach to the matching element in the live document so
        // the modal inherits the same color-scheme / wrapper classes.
        let target = null;
        let walker = newEl.parentElement;
        while (walker && !target) {
          if (walker.id) target = document.getElementById(walker.id);
          walker = walker.parentElement;
        }
        (target || document.body).appendChild(newEl);
      } else if (oldEl) {
        oldEl.remove();
      }
    }

    /* ── Helper: swap innerHTML of a container ── */
    swapInner(doc, selector) {
      const oldEl = document.querySelector(selector);
      const newEl = doc.querySelector(selector);
      if (oldEl && newEl) {
        oldEl.innerHTML = newEl.innerHTML;
      }
    }
  }

  customElements.define("grouped-variant-picker", GroupedVariantPicker);
}
